import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { DashboardService } from '../../Services/dashboard.services';

/** One status row rendered by the list view of the deviations box. */
interface MyDeviationRow {
  /** Translation key returned by the API, e.g. NEW_CASES. */
  statusText: string;
  /** new | open | done | rejected, used as the status class. */
  status: string;
  count: number;
}

/** Order the statuses are listed in, most recent state first. */
const STATUS_ORDER = ['NEW', 'OPEN', 'DONE', 'REJECTED'];

/**
 * List view of the deviations dashboard box: the status count for the deviations the logged in
 * user created, or is responsible for execution or follow-up of.
 */
@Component({
  selector: 'app-my-deviations-widget',
  templateUrl: './my-deviations-widget.component.html',
  styleUrls: ['./my-deviations-widget.component.scss'],
  standalone: false
})
export class MyDeviationsWidgetComponent implements OnInit, OnDestroy {
  rows: MyDeviationRow[] = [];
  loading = true;

  private subscriptions = new Subscription();

  constructor(
    private dashboardService: DashboardService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadRows();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get total(): number {
    return this.rows.reduce((sum, row) => sum + row.count, 0);
  }

  /**
   * Navigate to the deviation follow-up page, the same target as the chart view.
   */
  openDeviations(): void {
    const segments = this.router.url.split('/');
    segments[segments.length - 1] = 'follow-up';
    segments.push('deviations');

    this.router.navigate([segments.join('/')]);
  }

  private loadRows(): void {
    this.loading = true;

    this.subscriptions.add(
      this.dashboardService
        .getMyDeviationCount()
        .pipe(catchError(() => of([] as any[])))
        .subscribe((counts: any[]) => {
          this.rows = (counts || [])
            .map(count => this.toRow(count))
            .sort(
              (left, right) =>
                STATUS_ORDER.indexOf(left.status.toUpperCase()) -
                STATUS_ORDER.indexOf(right.status.toUpperCase())
            );

          this.loading = false;
        })
    );
  }

  private toRow(count: any): MyDeviationRow {
    const statusText: string = count.StatusText || '';

    return {
      statusText,
      status: statusText.replace('_CASES', '').toLowerCase(),
      count: count.Count
    };
  }
}
